import CameraFeed from "components/CameraFeed";
import React, { useContext, useEffect, useRef, useState } from "react";
import FooterControlTool from "components/footer-control-tools";
import { ConditionContext } from "hooks/ConditionContext";
import { CommonContext } from "hooks/CommonContext";
import ParticipantGridVideo from "./components/ParticipantGridVideo";
import clsx from "clsx";
import SharedScreenVideo from "./components/SharedScreenVideo";
import { getInitials } from "utils/commonUtils";
import useAspectRatioWithPercentage from "hooks/useAspectRatioWithPercentage";
import useIsMobile from "hooks/useIsMobile";
import useCameraCss from "hooks/useCameraCss";

const STYLES = `
  .hrm-root {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    background: #080d18;
    overflow: hidden;
    box-sizing: border-box;
  }

  .hrm-grid-area {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    gap: 6px;
    padding: 8px;
    overflow: hidden;
  }

  .hrm-main {
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
    border-radius: 14px;
    overflow: hidden;
    background: #0c1428;
    border: 1px solid #1a2540;
    position: relative;
  }

  .hrm-strip {
    flex: 0 0 280px;
    width: 280px;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-width: none;
    align-items: stretch;
  }
  .hrm-strip::-webkit-scrollbar { display: none; }

  .hrm-tile {
    border-radius: 12px;
    overflow: hidden;
    background: #0c1428;
    border: 1px solid #1a2540;
    flex-shrink: 0;
    width: 100%;
    aspect-ratio: 16 / 9;
    position: relative;
  }

  .hrm-footer { flex-shrink: 0; }

  .open-drawer-participent-section.hrm-root,
  .close-drawer-participent-section.hrm-root {
    display: flex !important;
    flex-direction: column !important;
    width: 100% !important;
    height: 100% !important;
    overflow: hidden !important;
  }

  /* Main pane — fixed width when drawer open, full when closed */
  .open-drawer-candidates-box.hrm-main {
    flex: 1 1 auto !important;
    min-width: 0 !important;
    min-height: 0 !important;
    width: auto !important;
    height: auto !important;
  }

  /* Strip — always 280px, never let external CSS collapse it */
  .open-drawer-interviewer-box.hrm-strip,
  .close-drawer-interviewer-box.hrm-strip,
  .flex-column.close-drawer-interviewer-box.hrm-strip {
    flex: 0 0 280px !important;
    width: 280px !important;
    min-width: 280px !important;
    max-width: 280px !important;
    display: flex !important;
    flex-direction: column !important;
    height: 100% !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
  }

  /* ── Mobile ──────────────────────────────────────────────────
     NOTE: hrm-root height is set via JS (rootRef useEffect) to
     window.innerHeight - offsetTop so it never overflows the
     viewport. The spacer below then works correctly.
  ─────────────────────────────────────────────────────────── */
  @media (max-width: 768px) {
    /* Transparent spacer — reserves space for the fixed footer bar.
       overflow:visible keeps the fixed child visible and tappable.
       No pointer-events:none — that blocks taps on the bar. */
    .hrm-footer {
      flex: 0 0 80px !important;
      height: 80px !important;
      min-height: 80px !important;
      max-height: 80px !important;
      background: transparent !important;
      overflow: visible !important;
    }

    .hrm-root .hrm-grid-area {
      flex-direction: column !important;
      padding: 6px 6px 0 !important;
      gap: 6px !important;
      overflow: hidden !important;
    }

    .hrm-root .hrm-grid-area .hrm-main {
      flex: 1 1 auto !important;
      width: 100% !important;
      min-height: 0 !important;
      height: auto !important;
      border-radius: 12px !important;
    }

    .hrm-root .hrm-grid-area .hrm-strip {
      flex: 0 0 110px !important;
      height: 110px !important;
      min-height: 110px !important;
      max-height: 110px !important;
      width: 100% !important;
      display: flex !important;
      flex-direction: row !important;
      flex-wrap: nowrap !important;
      overflow-x: auto !important;
      overflow-y: hidden !important;
      gap: 6px !important;
      align-items: stretch !important;
      scrollbar-width: thin;
      scrollbar-color: #1a2540 transparent;
    }
    .hrm-root .hrm-grid-area .hrm-strip::-webkit-scrollbar { height: 2px; }
    .hrm-root .hrm-grid-area .hrm-strip::-webkit-scrollbar-thumb { background: #253050; border-radius: 2px; }

    .hrm-root .hrm-grid-area .hrm-strip .hrm-tile {
      flex: 0 0 85px !important;
      width: 85px !important;
      min-width: 85px !important;
      max-width: 85px !important;
      height: 110px !important;
      min-height: 110px !important;
      max-height: 110px !important;
      aspect-ratio: unset !important;
    }
  }
`;

let _inj = false;
const inject = () => {
  if (_inj) return;
  const s = document.createElement("style");
  s.textContent = STYLES;
  document.head.appendChild(s);
  _inj = true;
};

const Index = () => {
  inject();

  const { openedComponentDrawer } = useContext(ConditionContext);
  const { participantOfMeeting, userOwnData, screenSharedStreams } =
    useContext(CommonContext);

  const [isCandidateJoined, setIsCandidateJoined] = useState(false);
  const [isParticipantExist, setIsParticipantExist] = useState(false);

  useEffect(() => {
    const joined = participantOfMeeting.filter((u) => u.isJoin);
    const candidates = participantOfMeeting.filter((u) => u.isCandidate && u.isJoin);
    setIsParticipantExist(joined.length > 0);
    setIsCandidateJoined(candidates.length > 0);
    // eslint-disable-next-line
  }, [
    screenSharedStreams.remoteStream,
    // eslint-disable-next-line
    participantOfMeeting.filter((u) => u.isJoin).length,
  ]);

  const { mainWrapperCss, mainCameraScreenCss, otherCameraScreenCss } =
    useCameraCss(openedComponentDrawer, isParticipantExist);

  const MainScreen = (
    <>
      {userOwnData.isCandidate || !isCandidateJoined ? (
        <div className="w-100 h-100">
          <CameraFeed isLgIcon={true} />
        </div>
      ) : (
        participantOfMeeting.map((item) =>
          !item.isScreenShare && item.isJoin && item.isCandidate ? (
            <div key={`video_box_${item.guid}`} className="h-100 w-100" style={{ position: "relative" }}>
              <ParticipantGridVideo
                userId={item.userId}
                remoteStream={item.remoteStream}
                userNameValue={item.userNameValue}
                isAudioOn={item.isAudioOn}
                isCameraOn={item.isCameraOn}
                colorCode={item.colorCode}
                latter={getInitials(item)}
                isLgIcon={true}
              />
            </div>
          ) : null
        )
      )}
    </>
  );

  const containerRef = useRef(null);
  const parentRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const heightPct = 100;

  const widthHeightBox = useAspectRatioWithPercentage({
    basePercentage: 100,
    isWidth: openedComponentDrawer.length === 0,
    parentRef: parentRef?.current,
  });

  useEffect(() => {
    const check = () => {
      if (containerRef.current)
        setIsOverflowing(containerRef.current.scrollHeight > containerRef.current.clientHeight);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [widthHeightBox]);

  const isDrawerOpen = openedComponentDrawer.length > 0;
  const isMobile = useIsMobile();
  const tileStyle = {};

  // ── Mobile height fix ───────────────────────────────────────────────────────
  // hrm-root uses height:100% which resolves to the parent's full height (often
  // 100vh). But the room title bar above hrm-root pushes it down, causing it to
  // overflow the viewport by ~title_bar_height px at the bottom. This means the
  // spacer for the fixed footer bar lands outside the viewport and tiles get
  // covered. We fix this by measuring the element's actual offsetTop and setting
  // height = window.innerHeight - offsetTop so it ends exactly at viewport bottom.
  // This only runs on mobile — desktop is completely unaffected.
  const rootRef = useRef(null);
  useEffect(() => {
    if (!isMobile) return;
    const updateHeight = () => {
      if (!rootRef.current) return;
      const top = rootRef.current.getBoundingClientRect().top;
      const h = window.innerHeight - top;
      rootRef.current.style.height = `${h}px`;
      rootRef.current.style.maxHeight = `${h}px`;
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, [isMobile]);
  // ───────────────────────────────────────────────────────────────────────────

  return (
    <div ref={rootRef} className={`hrm-root ${mainWrapperCss}`}>
      <div className="hrm-grid-area grid-room-box">

        {/* Main pane */}
        <div className={clsx("text-center", mainCameraScreenCss, "hrm-main")}>
          {screenSharedStreams.remoteStream ? (
            <SharedScreenVideo
              screenShardName={screenSharedStreams.userNameValue}
              key={`screen_${screenSharedStreams.userId}`}
              userId={screenSharedStreams.userId}
              remoteStream={screenSharedStreams.remoteStream}
            />
          ) : MainScreen}
        </div>

        {/* Sidebar strip */}
        <div
          ref={containerRef}
          className={clsx(
            "hrm-strip",
            !isOverflowing && "justify-content-center align-items-center",
            isMobile ? "w-100 mobile-view-video-grid-height" : otherCameraScreenCss
          )}
        >
          {participantOfMeeting.map((item) =>
            !item.isScreenShare && !item.isCandidate && item.isJoin ? (
              <div key={`video_box_${item.guid}`} ref={parentRef} style={tileStyle} className="hrm-tile p-1">
                <ParticipantGridVideo
                  userId={item.userId} remoteStream={item.remoteStream}
                  userNameValue={item.userNameValue} isAudioOn={item.isAudioOn}
                  isCameraOn={item.isCameraOn} colorCode={item.colorCode}
                  latter={(item.fname || item.firstName || "").charAt(0) + (item.lname || item.lastName || "").charAt(0)}
                />
              </div>
            ) : null
          )}

          {!(userOwnData.isCandidate || !isCandidateJoined) && (
            <div ref={parentRef} style={tileStyle} className="hrm-tile p-1">
              <CameraFeed />
            </div>
          )}

          {isMobile && screenSharedStreams.remoteStream && (
            <div style={tileStyle} className="hrm-tile p-1">
              <SharedScreenVideo
                screenShardName={screenSharedStreams.userNameValue}
                key={`screen_m_${screenSharedStreams.userId}`}
                userId={screenSharedStreams.userId}
                remoteStream={screenSharedStreams.remoteStream}
              />
            </div>
          )}

          {screenSharedStreams.remoteStream && (
            <>
              {userOwnData.isCandidate || !isCandidateJoined ? (
                <div ref={isMobile ? null : parentRef} style={tileStyle} className="hrm-tile p-1">
                  <CameraFeed />
                </div>
              ) : (
                participantOfMeeting.map((item) =>
                  !item.isScreenShare && item.isJoin && item.isCandidate ? (
                    <div
                      key={`video_ss_${item.guid}`}
                      ref={isMobile ? null : parentRef}
                      style={{
                        position: "relative",
                        width: isDrawerOpen ? `${widthHeightBox}px` : `${heightPct}%`,
                        height: isDrawerOpen ? `${heightPct}%` : `${widthHeightBox}px`,
                      }}
                      className="hrm-tile p-1"
                    >
                      <ParticipantGridVideo
                        userId={item.userId} remoteStream={item.remoteStream}
                        userNameValue={item.userNameValue} isAudioOn={item.isAudioOn}
                        isCameraOn={item.isCameraOn} colorCode={item.colorCode}
                        latter={(item.fname || item.firstName || "").charAt(0) + (item.lname || item.lastName || "").charAt(0)}
                      />
                    </div>
                  ) : null
                )
              )}
            </>
          )}
        </div>
      </div>

      {/* Footer — always pinned */}
      <div className="hrm-footer">
        <FooterControlTool />
      </div>
    </div>
  );
};

export default Index;